import { memo, useState, useRef, useEffect, Fragment } from "react";
import { useEditorState } from "@tiptap/react";
import type { Editor } from "@tiptap/react";
import {
  Bold, Italic, Underline, Code,
  Heading1, Heading2, Heading3,
  List, ListOrdered, ListTodo, Quote,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Undo2, Redo2, Strikethrough, Highlighter, Baseline,
  type LucideIcon,
} from "lucide-react";
import { CV } from "@/config/canvasSizing";

interface Props {
  editor: Editor;
  variant?: "light" | "dark";
}

type BoolActiveKey =
  | "bold" | "italic" | "underline" | "code" | "strike"
  | "h1" | "h2" | "h3"
  | "bulletList" | "orderedList" | "taskList" | "blockquote"
  | "alignLeft" | "alignCenter" | "alignRight" | "alignJustify";

interface ActiveState extends Record<BoolActiveKey, boolean> {
  highlightColor: string | null;
  textColor: string | null;
  canUndo: boolean;
  canRedo: boolean;
}

interface ToggleBtn {
  label: string;
  icon: LucideIcon;
  cmd: (e: Editor) => void;
  activeKey: BoolActiveKey;
}

const GROUPS: ToggleBtn[][] = [
  [
    { label: "Bold",          icon: Bold,          cmd: e => e.chain().toggleBold().run(),                  activeKey: "bold" },
    { label: "Italic",        icon: Italic,        cmd: e => e.chain().toggleItalic().run(),                activeKey: "italic" },
    { label: "Underline",     icon: Underline,     cmd: e => e.chain().toggleUnderline().run(),             activeKey: "underline" },
    { label: "Strikethrough", icon: Strikethrough, cmd: e => e.chain().toggleStrike().run(),                activeKey: "strike" },
    { label: "Code",          icon: Code,          cmd: e => e.chain().toggleCode().run(),                  activeKey: "code" },
  ],
  [
    { label: "Heading 1", icon: Heading1, cmd: e => e.chain().toggleHeading({ level: 1 }).run(), activeKey: "h1" },
    { label: "Heading 2", icon: Heading2, cmd: e => e.chain().toggleHeading({ level: 2 }).run(), activeKey: "h2" },
    { label: "Heading 3", icon: Heading3, cmd: e => e.chain().toggleHeading({ level: 3 }).run(), activeKey: "h3" },
  ],
  [
    { label: "Bullet list",  icon: List,         cmd: e => e.chain().toggleBulletList().run(),   activeKey: "bulletList" },
    { label: "Ordered list", icon: ListOrdered,  cmd: e => e.chain().toggleOrderedList().run(),  activeKey: "orderedList" },
    { label: "Task list",    icon: ListTodo,     cmd: e => e.chain().toggleTaskList().run(),      activeKey: "taskList" },
    { label: "Blockquote",   icon: Quote,        cmd: e => e.chain().toggleBlockquote().run(),   activeKey: "blockquote" },
  ],
  [
    { label: "Align left",   icon: AlignLeft,    cmd: e => e.chain().setTextAlign("left").run(),    activeKey: "alignLeft" },
    { label: "Align center", icon: AlignCenter,  cmd: e => e.chain().setTextAlign("center").run(),  activeKey: "alignCenter" },
    { label: "Align right",  icon: AlignRight,   cmd: e => e.chain().setTextAlign("right").run(),   activeKey: "alignRight" },
    { label: "Justify",      icon: AlignJustify, cmd: e => e.chain().setTextAlign("justify").run(), activeKey: "alignJustify" },
  ],
];

const HIGHLIGHT_COLORS = [
  "#fef08a", "#bbf7d0", "#bae6fd", "#fbcfe8",
  "#fed7aa", "#a5f3fc", "#ddd6fe", "#fca5a5",
];

const TEXT_COLORS: (string | null)[] = [
  "#ef4444", "#f97316", "#f59e0b", "#22c55e",
  "#3b82f6", "#8b5cf6", "#ec4899", null,
];

// ── Color picker popover ───────────────────────────────────────────────────────

interface ColorPickerProps {
  icon: LucideIcon;
  label: string;
  colors: (string | null)[];
  activeColor: string | null;
  onPick: (color: string | null) => void;
  dark: boolean;
  btnBase: string;
}

function ColorPicker({ icon: Icon, label, colors, activeColor, onPick, dark, btnBase }: ColorPickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const trackColor = activeColor ?? (dark ? "#3f3f46" : "#d4d4d8");

  return (
    <div ref={ref} className="relative" data-testid={`color-picker-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <button
        title={label}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((o) => !o)}
        className={`${btnBase} relative`}
      >
        <div className="relative flex flex-col items-center">
          <Icon size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} />
          <span className="absolute -bottom-1 left-0 right-0 h-[3px] rounded-full" style={{ background: trackColor }} />
        </div>
      </button>

      {open && (
        <div
          className={`absolute top-full left-0 mt-1.5 z-[9999] p-2 rounded-xl shadow-2xl border flex flex-wrap gap-1.5 ${
            dark ? "bg-zinc-800 border-white/10" : "bg-white border-zinc-200"
          }`}
          style={{ width: 116 }}
          data-testid={`color-picker-${label.toLowerCase().replace(/\s/g, "-")}-popover`}
        >
          {colors.map((c, i) =>
            c === null ? (
              <button
                key={i}
                title="Default"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onPick(null); setOpen(false); }}
                className={`w-5 h-5 rounded border flex items-center justify-center text-[8px] font-bold transition-transform hover:scale-110 ${
                  dark ? "border-white/20 text-zinc-400 bg-zinc-700" : "border-zinc-300 text-zinc-400 bg-white"
                }`}
              >
                ✕
              </button>
            ) : (
              <button
                key={i}
                title={c}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onPick(c); setOpen(false); }}
                className="w-5 h-5 rounded border border-black/10 transition-transform hover:scale-110 shadow-sm"
                style={{ background: c }}
              />
            )
          )}
        </div>
      )}
    </div>
  );
}

// ── Main toolbar ───────────────────────────────────────────────────────────────

function EditorToolbar({ editor, variant = "light" }: Props) {
  const active = useEditorState({
    editor,
    selector: (ctx): ActiveState => ({
      bold:         ctx.editor.isActive("bold"),
      italic:       ctx.editor.isActive("italic"),
      underline:    ctx.editor.isActive("underline"),
      code:         ctx.editor.isActive("code"),
      strike:       ctx.editor.isActive("strike"),
      h1:           ctx.editor.isActive("heading", { level: 1 }),
      h2:           ctx.editor.isActive("heading", { level: 2 }),
      h3:           ctx.editor.isActive("heading", { level: 3 }),
      bulletList:   ctx.editor.isActive("bulletList"),
      orderedList:  ctx.editor.isActive("orderedList"),
      taskList:     ctx.editor.isActive("taskList"),
      blockquote:   ctx.editor.isActive("blockquote"),
      alignLeft:    ctx.editor.isActive({ textAlign: "left" }),
      alignCenter:  ctx.editor.isActive({ textAlign: "center" }),
      alignRight:   ctx.editor.isActive({ textAlign: "right" }),
      alignJustify: ctx.editor.isActive({ textAlign: "justify" }),
      highlightColor: (ctx.editor.getAttributes("highlight").color as string | undefined) ?? null,
      textColor:      (ctx.editor.getAttributes("textStyle").color as string | undefined) ?? null,
      canUndo: ctx.editor.can().undo(),
      canRedo: ctx.editor.can().redo(),
    }),
  });

  const dark = variant === "dark";

  const btnBase = dark
    ? "p-1.5 rounded transition-colors text-zinc-500 hover:text-zinc-200 hover:bg-white/10"
    : "p-1.5 rounded transition-colors text-canvas-muted hover:text-canvas-text hover:bg-black/5";

  const btnActive  = dark ? "bg-white/15 text-zinc-100" : "bg-canvas-accent/10 text-canvas-accent";
  const btnDisabled = "opacity-25 cursor-not-allowed";
  const divider    = dark ? "w-px h-4 bg-white/10 mx-0.5 shrink-0" : "w-px h-4 bg-black/10 mx-0.5 shrink-0";

  return (
    <div className="flex items-center gap-0.5 flex-wrap" data-testid="editor-toolbar">
      {/* Toggle button groups */}
      {GROUPS.map((group, gi) => (
        <Fragment key={gi}>
          {group.map(({ label, icon: Icon, cmd, activeKey }) => (
            <button
              key={label}
              title={label}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => cmd(editor)}
              className={`${btnBase} ${active?.[activeKey] ? btnActive : ""}`}
              data-testid={`toolbar-btn-${label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <Icon size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} />
            </button>
          ))}
          <div className={divider} />
        </Fragment>
      ))}

      {/* Text color picker */}
      <ColorPicker
        icon={Baseline}
        label="Text color"
        colors={TEXT_COLORS}
        activeColor={active?.textColor ?? null}
        onPick={(c) => c ? editor.chain().setColor(c).run() : editor.chain().unsetColor().run()}
        dark={dark}
        btnBase={btnBase}
      />

      {/* Highlight picker */}
      <ColorPicker
        icon={Highlighter}
        label="Highlight"
        colors={HIGHLIGHT_COLORS}
        activeColor={active?.highlightColor ?? null}
        onPick={(c) => c ? editor.chain().setHighlight({ color: c }).run() : editor.chain().unsetHighlight().run()}
        dark={dark}
        btnBase={btnBase}
      />

      <div className={divider} />

      {/* Undo */}
      <button
        title="Undo"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => editor.chain().undo().run()}
        disabled={!active?.canUndo}
        className={`${btnBase} ${!active?.canUndo ? btnDisabled : ""}`}
        data-testid="toolbar-btn-undo"
      >
        <Undo2 size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} />
      </button>

      {/* Redo */}
      <button
        title="Redo"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => editor.chain().redo().run()}
        disabled={!active?.canRedo}
        className={`${btnBase} ${!active?.canRedo ? btnDisabled : ""}`}
        data-testid="toolbar-btn-redo"
      >
        <Redo2 size={CV.toolbar.iconSize} strokeWidth={CV.toolbar.iconStrokeWidth} />
      </button>
    </div>
  );
}

export default memo(EditorToolbar);
